if (!customElements.get('meko-floating-cart')) {
  customElements.define('meko-floating-cart', class extends HTMLElement {
    connectedCallback() {
      this.main = this.closest('product-info');
      this.primary = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!this.main || !this.primary || !this.form) return;

      this.dock = this.querySelector('[data-floating-dock]');
      this.panel = this.querySelector('dialog');
      this.picker = this.main.querySelector('variant-selects');
      this.price = this.main.querySelector(`#price-${this.dataset.section}`);
      this.media = window.matchMedia('(min-width: 990px)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };

      this.addEventListener('change', (event) => this.changeOption(event), options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-floating-open]')) this.openPanel();
        if (event.target.closest('[data-floating-close]')) this.panel?.close();
        if (event.target.closest('[data-floating-submit]')) this.addToCart();
      }, options);
      this.panel?.addEventListener('click', (event) => {
        if (event.target !== this.panel) return;
        const rect = this.panel.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          this.panel.close();
        }
      }, options);
      this.panel?.addEventListener('close', () => {
        this.classList.remove('is-choosing');
        this.querySelector('[data-floating-open]').setAttribute('aria-expanded', 'false');
      }, options);
      this.media.addEventListener('change', () => {
        this.panel?.close();
        this.updateVisibility();
      }, options);
      window.addEventListener('resize', () => this.scheduleSync(), options);
      this.main.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')) {
          this.pendingVariant = true;
          this.sync();
        }
      }, options);

      this.primaryObserver = new IntersectionObserver(([entry]) => {
        this.primaryVisible = entry.isIntersecting;
        this.updateVisibility();
      }, { threshold: 0.1 });
      this.primaryObserver.observe(this.primary);

      this.sourceObserver = new MutationObserver((records) => {
        if (records.some(({ target }) => !this.contains(target) && (
          this.main.querySelector('variant-selects') !== this.picker ||
          this.form.closest('product-form').contains(target) ||
          this.price?.contains(target)
        ))) this.scheduleSync();
      });
      this.sourceObserver.observe(this.main, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'value'],
      });

      this.overlayObserver = new MutationObserver(() => this.updateVisibility());
      document.querySelectorAll('cart-drawer, header-drawer details, details-modal details, modal-dialog').forEach((node) => {
        this.overlayObserver.observe(node, { attributes: true, attributeFilter: ['class', 'open'] });
      });
      this.bodyObserver = new MutationObserver(() => this.scheduleSync());
      this.bodyObserver.observe(document.body, { childList: true });
      this.resizeObserver = new ResizeObserver(() => this.updateVisibility());
      this.resizeObserver.observe(this.dock);
      this.sync();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.primaryObserver?.disconnect();
      this.sourceObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.bodyObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.syncFrame);
      this.panel?.close();
      document.body.classList.remove('meko-floating-cart-active');
      document.body.style.removeProperty('--meko-floating-space');
    }

    scheduleSync() {
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => this.sync());
    }

    get groups() {
      return Array.from(this.picker?.querySelectorAll('fieldset, .product-form__input--dropdown') || []);
    }

    changeOption(event) {
      const mirror = event.target.closest('[data-floating-option]');
      if (!mirror) return;
      const group = this.groups[Number(mirror.dataset.floatingOption)];
      const master = group?.querySelector('select') || Array.from(group?.querySelectorAll('input[type="radio"]') || []).find((input) => input.value === mirror.value);
      if (!master) return;
      if (master.tagName === 'SELECT') master.value = mirror.value;
      else master.checked = true;
      this.returnFocus = mirror.id;
      this.selectionScroll = { left: window.scrollX, top: window.scrollY };
      master.dispatchEvent(new Event('change', { bubbles: true }));
    }

    sync() {
      if (!this.isConnected) return;
      const nextPicker = this.main.querySelector('variant-selects');
      const pickerUpdated = nextPicker !== this.picker;
      if (pickerUpdated) {
        this.picker = nextPicker;
        this.pendingVariant = false;
      }

      const selected = [];
      this.groups.forEach((group, index) => {
        const input = group.querySelector('input:checked, select');
        if (!input) return;
        selected.push(input.value);
        this.querySelectorAll(`[data-floating-option="${index}"]`).forEach((mirror) => {
          mirror.value = input.value;
          mirror.title = input.value;
          Array.from(mirror.options).forEach((option) => {
            const master = Array.from(group.querySelectorAll('input[type="radio"], option')).find((node) => node.value === option.value);
            option.disabled = Boolean(master?.disabled);
            const unavailable = master?.classList.contains('disabled');
            option.textContent = unavailable ? `${option.value} - ${window.variantStrings.soldOut}` : option.value;
          });
        });
      });
      const summary = this.querySelector('[data-floating-summary]');
      if (summary) summary.textContent = selected.join(' / ');
      const amount = this.price?.querySelector('.meko-product-price-row__selling, .price-item--sale, .price-item--regular');
      if (amount) this.querySelectorAll('[data-floating-price]').forEach((node) => { node.textContent = amount.textContent.trim(); });
      const image = this.querySelector('.meko-floating-cart__image');
      const selectedVariant = this.picker?.querySelector('[data-selected-variant]');
      if (image && selectedVariant) {
        const variant = JSON.parse(selectedVariant.textContent);
        const source = variant?.featured_image?.src;
        if (source && image.dataset.variantImage !== source) {
          const url = new URL(source, window.location.href);
          url.searchParams.set('width', '120');
          image.removeAttribute('srcset');
          image.src = url.href;
          image.dataset.variantImage = source;
        }
      }

      const loading = this.primary.classList.contains('loading');
      const disabled = this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true' || this.pendingVariant;
      this.querySelectorAll('[data-floating-submit]').forEach((button) => {
        button.disabled = Boolean(disabled);
        button.setAttribute('aria-busy', String(Boolean(loading || this.pendingVariant)));
        button.querySelector('.meko-floating-cart__spinner').hidden = !loading;
        button.querySelector('[data-floating-button-text]').textContent = this.primary.disabled && !this.pendingVariant
          ? this.primary.querySelector('span').textContent.trim()
          : this.dataset.label;
      });
      const errorWrapper = this.form.closest('product-form').querySelector('.product-form__error-message-wrapper');
      const error = this.querySelector('[data-floating-error]');
      error.textContent = errorWrapper && !errorWrapper.hidden ? errorWrapper.textContent.trim() : '';
      error.hidden = !error.textContent;

      // Dawn focuses its original option after rendering. Keep a dock change in place.
      if (pickerUpdated && this.returnFocus) {
        const targetId = this.returnFocus;
        const scrollPosition = this.selectionScroll;
        window.scrollTo({ ...scrollPosition, behavior: 'instant' });
        this.querySelector(`#${CSS.escape(targetId)}`)?.focus({ preventScroll: true });
        this.returnFocus = null;
      }
      this.updateVisibility();
    }

    updateVisibility() {
      const preview = document.getElementById('PBarNextFrame');
      if (preview && preview !== this.previewFrame) {
        this.previewFrame = preview;
        this.resizeObserver.observe(preview.parentElement);
      }
      const previewRect = preview?.parentElement.getBoundingClientRect();
      const previewExpanded = previewRect && previewRect.height > window.innerHeight / 2;
      const previewOffset = previewRect && !previewExpanded ? Math.max(0, window.innerHeight - previewRect.top) : 0;
      this.style.setProperty('--floating-preview-offset', `${previewOffset}px`);
      const overlayOpen = document.querySelector('cart-drawer.active, header-drawer details[open], details-modal details[open], modal-dialog[open]');
      const enabled = this.media.matches ? this.dataset.desktop === 'true' : this.dataset.mobile === 'true';
      const visible = enabled && !overlayOpen && !previewExpanded && (this.dataset.visibility === 'always' || !this.primaryVisible);
      this.dock.hidden = !visible;
      document.body.classList.toggle('meko-floating-cart-active', Boolean(visible));
      if (visible) {
        document.body.style.setProperty('--meko-floating-space', `${this.dock.offsetHeight + previewOffset + 28}px`);
      } else {
        document.body.style.removeProperty('--meko-floating-space');
        this.panel?.close();
      }
    }

    openPanel() {
      if (!this.panel) return;
      this.sync();
      this.classList.add('is-choosing');
      this.querySelector('[data-floating-open]').setAttribute('aria-expanded', 'true');
      this.panel.showModal();
      this.panel.querySelector('select')?.focus({ preventScroll: true });
    }

    addToCart() {
      if (this.pendingVariant || this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true') return;
      this.panel?.close();
      this.primary.click();
      this.sync();
    }
  });
}
